<script setup lang="ts">
    import IconChevronUp from '@/components/icons/IconChevronUp.vue';
    import IconChevronDown from '@/components/icons/IconChevronDown.vue';
    import { hsvToHsl, hslToRgb, rgbToHexa, hexaToRgb, rgbToHsv, hexToPercent, percentToHex } from '@/utils/colors';
    import BaseColorPickerInputHsl from '@/components/BaseColorPickerInputHsl.vue';
    import BaseColorPickerInputHex from '@/components/BaseColorPickerInputHex.vue';
    import BaseColorPickerInputRgb from '@/components/BaseColorPickerInputRgb.vue';
    import { computed, ref, onMounted, onUnmounted } from 'vue';
    import type { IHSL, IHSV, IRGB } from '@/typescript/interfaces/colors';

    enum InputMethods {
        HSL = 'hsl',
        RGB = 'rgb',
        HEX = 'hex',
    }

    const HEX_MAX_LENGTH = 9;

    const hexa = defineModel<string>({ default: '#000000' });

    const { alpha = false } = defineProps<{ alpha?: boolean }>();

    const currentInputMethod = ref(InputMethods.HEX);

    const hsv = ref<IHSV>({ h: 0, s: 0, v: 0 });
    const hsl = ref<IHSL>({ h: 0, s: 0, l: 0 });
    const rgb = ref<IRGB>({ r: 0, g: 0, b: 0 });
    const alphaInputValue = ref(100);
    const picker = ref<HTMLDivElement>();

    onMounted(() => {
        updateHexa(hexa.value);
        document.addEventListener('pointerup', stopDrag);
    });

    onUnmounted(() => {
        document.removeEventListener('pointermove', updateColorOnClick);
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('scroll', onTouchMove);
        document.removeEventListener('pointerup', stopDrag);
    });

    const colorStyle = computed(() => {
        return {
            backgroundColor: `hsl(${hsl.value.h}, ${hsl.value.s}%, ${hsl.value.l}%)`,
        };
    });

    const cursorStyle = computed(() => {
        return {
            left: `${hsv.value.s}%`,
            bottom: `${hsv.value.v}%`,
            ...colorStyle.value,
        };
    });

    const opacityColor = computed(() => `rgb(${rgb.value.r},${rgb.value.g},${rgb.value.b})`);

    const inputLabel = computed(() => {
        if (alpha) {
            return `${currentInputMethod.value}a`;
        }
        return currentInputMethod;
    });

    const alphaValue = computed(() => {
        if (alpha) {
            return alphaInputValue.value;
        }
        return undefined;
    });

    function changeInputMethod({ forward }: { forward: boolean }) {
        const inputMethodsOrder = Object.values(InputMethods);
        const currentIndex = inputMethodsOrder.indexOf(currentInputMethod.value);
        let nextIndex = forward ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex < 0) {
            nextIndex = inputMethodsOrder.length - 1;
        } else if (nextIndex === inputMethodsOrder.length) {
            nextIndex = 0;
        }
        currentInputMethod.value = inputMethodsOrder[nextIndex];
    }

    function updateColorOnClick(e: MouseEvent | PointerEvent) {
        e.stopPropagation();
        e.preventDefault();
        if (!picker.value) {
            return;
        }
        const target = picker.value;
        const targetPosition = target.getBoundingClientRect();

        let relativeX = e.x - targetPosition.left;
        let relativeY = e.y - targetPosition.top;

        if (relativeX > target.clientWidth) {
            relativeX = target.clientWidth;
        } else if (relativeX < 0) {
            relativeX = 0;
        }

        if (relativeY > target.clientHeight) {
            relativeY = target.clientHeight;
        } else if (relativeY < 0) {
            relativeY = 0;
        }

        hsv.value.v = Math.round((1 - relativeY / target.clientHeight) * 100);
        hsv.value.s = Math.round((relativeX / target.clientWidth) * 100);

        hsl.value = hsvToHsl(hsv.value);
        rgb.value = hslToRgb(hsl.value);
        hexa.value = rgbToHexa(rgb.value, alphaValue.value);
    }

    function updateHue(hue: number) {
        hsv.value.h = hue;
        hsl.value.h = hue;
        rgb.value = hslToRgb(hsl.value);
        hexa.value = rgbToHexa(rgb.value, alphaValue.value);
    }

    function updateAlphaSlider(event: Event) {
        updateAlpha(Number((event.target as HTMLInputElement).value));
    }

    function updateAlphaInput(event: Event) {
        updateAlpha(Number((event.target as HTMLInputElement).value) * 100);
    }

    function updateAlpha(newAlpha: number) {
        alphaInputValue.value = newAlpha;
        const alphaHex = percentToHex(alphaInputValue.value);
        hexa.value = hexa.value.slice(0, 7) + alphaHex;
    }

    function updateHsl(newHsl: IHSL) {
        hsl.value = newHsl;
        rgb.value = hslToRgb(newHsl);
        hsv.value = rgbToHsv(rgb.value);
        hexa.value = rgbToHexa(rgb.value, alphaValue.value);
    }

    function updateRgb(newRgb: IRGB) {
        rgb.value = newRgb;
        hsv.value = rgbToHsv(rgb.value);
        hsl.value = hsvToHsl(hsv.value);
        hexa.value = rgbToHexa(rgb.value, alphaValue.value);
    }

    function updateHexa(newHexa: string) {
        if (newHexa.length === HEX_MAX_LENGTH) {
            const hexaAlpha = newHexa.substring(7);
            alphaInputValue.value = hexToPercent(hexaAlpha);
        } else {
            alphaInputValue.value = 100;
        }
        hexa.value = newHexa;
        const newRgb = hexaToRgb(newHexa);
        if (!newRgb) {
            return;
        }
        rgb.value = newRgb;
        hsv.value = rgbToHsv(rgb.value);
        hsl.value = hsvToHsl(hsv.value);
    }

    function startDrag() {
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('scroll', onTouchMove, { passive: false });
        document.addEventListener('pointermove', updateColorOnClick);
    }

    function stopDrag() {
        document.removeEventListener('pointermove', updateColorOnClick);
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('scroll', onTouchMove);
    }

    function onTouchMove(e: Event) {
        e.preventDefault();
        e.stopPropagation();
    }
</script>
<template>
    <div :class="['flex w-64 flex-col items-center justify-between gap-2 pb-2', alpha && 'h-72', !alpha && 'h-64']">
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
        <div
            ref="picker"
            data-test="color-picker"
            class="h-44 w-full rounded-md hover:cursor-pointer"
            :style="`background-color: hsl(${hsv.h}, 100%, 50%)`"
            @click="updateColorOnClick"
            @pointerdown="startDrag"
            @pointerup="stopDrag"
        >
            <div class="sat-gradient pointer-events-none h-full w-full" />
            <div class="lightness-gradient pointer-events-none absolute left-0 top-0 h-full w-full" />
            <div class="absolute left-0 top-0 h-full w-full">
                <div
                    data-test="color-cursor"
                    class="absolute -mb-1.5 -ml-1.5 h-3 w-3 rounded-full border-2 border-white"
                    :style="cursorStyle"
                    @pointerdown="startDrag"
                    @pointerup="stopDrag"
                ></div>
            </div>
        </div>
        <input
            :value="hsv.h"
            :aria-label="$t('colorpicker_aria_hue')"
            min="0"
            max="360"
            type="range"
            class="hue-gradient h-2 w-60 appearance-none rounded-full hover:cursor-pointer"
            @input="updateHue(Number(($event.target as HTMLInputElement).value))"
        />
        <input
            v-if="alpha"
            :value="alphaInputValue"
            :aria-label="$t('colorpicker_aria_alpha')"
            min="0"
            max="100"
            type="range"
            class="alpha-gradient h-2 w-60 appearance-none rounded-full hover:cursor-pointer"
            @input="updateAlphaSlider"
        />
        <div class="flex w-full flex-row items-center justify-between gap-2 px-2">
            <div :class="['flex h-8 w-16 flex-none flex-row items-center justify-between', 'border-border rounded-md border px-1']">
                <div class="capitalize">{{ inputLabel }}</div>
                <div class="flex flex-col">
                    <IconChevronUp
                        data-test="switch-input"
                        class="h-2 hover:cursor-pointer"
                        @click="changeInputMethod({ forward: false })"
                    />
                    <IconChevronDown
                        class="h-2 hover:cursor-pointer"
                        @click="changeInputMethod({ forward: true })"
                    />
                </div>
            </div>
            <BaseColorPickerInputHsl
                v-if="currentInputMethod === InputMethods.HSL"
                :model-value="hsl"
                @update:model-value="updateHsl"
            />
            <BaseColorPickerInputHex
                v-if="currentInputMethod === InputMethods.HEX"
                :model-value="hexa"
                @update:model-value="updateHexa"
            />
            <BaseColorPickerInputRgb
                v-if="currentInputMethod === InputMethods.RGB"
                :model-value="rgb"
                @update:model-value="updateRgb"
            />
            <span
                v-if="currentInputMethod !== InputMethods.HEX && alpha"
                class="flex flex-row items-center gap-2"
            >
                <input
                    class="border-theme-background h-8 w-10 appearance-none rounded-md border text-center"
                    :aria-label="$t('colorpicker_aria_opacity')"
                    data-test="opacity-value"
                    :value="alphaInputValue / 100"
                    @change="updateAlphaInput"
                />
            </span>
        </div>
    </div>
</template>
<style scoped>
    .hue-gradient {
        background: linear-gradient(
            to right,
            hsl(0deg 100% 50%),
            hsl(45deg 100% 50%),
            hsl(90deg 100% 50%),
            hsl(135deg 100% 50%),
            hsl(180deg 100% 50%),
            hsl(225deg 100% 50%),
            hsl(270deg 100% 50%),
            hsl(315deg 100% 50%),
            hsl(360deg 100% 50%)
        );
    }

    .alpha-gradient {
        background:
            linear-gradient(to right, transparent, v-bind(opacityColor)),
            repeating-conic-gradient(#e1e1e6 0% 25%, white 0% 50%) 50% / 6px 6px;
    }

    .sat-gradient {
        background: linear-gradient(to right, rgb(255 255 255 / 10000%), rgb(255 255 255 / 0%));
    }

    .lightness-gradient {
        background: linear-gradient(to bottom, rgb(0 0 0 / 0%), rgb(0 0 0 / 10000%));
    }

    input[type='range']::-webkit-slider-runnable-track {
        -webkit-appearance: none;
        background: transparent;
    }

    input[type='range']::-moz-range-thumb {
        appearance: none;
        background: none;
        width: 8px;
        height: 8px;
        border: 2px solid white;
        border-radius: 8px;
    }

    input[type='range']::-webkit-slider-thumb {
        appearance: none;
        background: none;
        width: 10px;
        height: 10px;
        border: 2px solid white;
        border-radius: 10px;
    }
</style>
